import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useForm, FormProvider, useFieldArray, useWatch } from "react-hook-form";
import { Box, Typography, Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomDatePicker from "components/common/DatePicker";
import Button from "components/common/Button";
import Text from "components/common/Text";
import Select from "components/common/Select";
import { AlertType } from "components/common/Snackbar";
import ConfirmChargeModal from "./ConfirmChargeModal";
import ResultModal from "./ResultModal";

import { createOneTimeCharge } from "services/customer";
import { showSnackbar } from "store/actions/Utility";
import ONE_TIME_INVOICE_ITEM_TYPE from "components/common/CreateOneTimeChargeModal/enums";

import "./CreateOneTimeChargeModal.scss";

const allChargeTypeOptions = [
  { value: ONE_TIME_INVOICE_ITEM_TYPE.Equipment, label: "Equipment" },
  { value: ONE_TIME_INVOICE_ITEM_TYPE.Upsell, label: "Upsell" },
  { value: ONE_TIME_INVOICE_ITEM_TYPE.LateFee, label: "Late Fee" },
  { value: ONE_TIME_INVOICE_ITEM_TYPE.HolidayCompensation, label: "Holiday Compensation" },
  { value: ONE_TIME_INVOICE_ITEM_TYPE.Miscellaneous, label: "Miscellaneous" },
];

function CreateOneTimeChargeModal({ open, onClose, onConfirm, onViewCharge = null }) {
  const dispatch = useDispatch();
  const { id: customerId } = useParams();

  const lastChargeItemRef = useRef(null);
  const [shouldScrollToLast, setShouldScrollToLast] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formDataForConfirm, setFormDataForConfirm] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const methods = useForm({
    defaultValues: {
      charges: [
        {
          chargeType: "",
          employeeName: "",
          fee: undefined,
          monthlyFee: undefined,
          implementationFee: undefined,
          discount: undefined,
        },
      ],
    },
    mode: "onChange",
  });

  const { control, handleSubmit, watch, reset, setValue } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "charges",
  });

  const watchedCharges = watch("charges");

  const watchedChargeTypes = useWatch({
    control,
    name: "charges",
    defaultValue: [],
  });

  const prevChargeTypesRef = useRef({});

  useEffect(() => {
    if (watchedChargeTypes && Array.isArray(watchedChargeTypes)) {
      watchedChargeTypes.forEach((charge, index) => {
        if (charge && charge.chargeType) {
          const currentChargeType = charge.chargeType;
          const previousChargeType = prevChargeTypesRef.current[index];

          if (previousChargeType && previousChargeType !== currentChargeType) {
            setValue(`charges.${index}.fee`, "", { shouldValidate: false });
            setValue(`charges.${index}.monthlyFee`, "", { shouldValidate: false });
            setValue(`charges.${index}.implementationFee`, "", { shouldValidate: false });
            setValue(`charges.${index}.discount`, "", { shouldValidate: false });

            setValue(`charges.${index}.employeeName`, "", { shouldValidate: false });
            setValue(`charges.${index}.notes`, "", { shouldValidate: false });
            setValue(`charges.${index}.startDate`, null, { shouldValidate: false });
            setValue(`charges.${index}.endDate`, null, { shouldValidate: false });
          }

          prevChargeTypesRef.current[index] = currentChargeType;
        }
      });
    }
  }, [watchedChargeTypes, setValue]);

  useEffect(() => {
    if (shouldScrollToLast && lastChargeItemRef.current) {
      // for reviewers -> important comment it is needed - Small delay to ensure the DOM has updated
      setTimeout(() => {
        lastChargeItemRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
        setShouldScrollToLast(false);
      }, 150);
    }
  }, [shouldScrollToLast, fields.length]);

  const calculateTotal = () => {
    return watchedCharges.reduce((total, charge) => {
      let chargeTotal = 0;

      if (isUpsellCharge(charge.chargeType)) {
        const monthlyFee = Number(charge.monthlyFee) || 0;
        const implementationFee = Number(charge.implementationFee) || 0;
        chargeTotal = monthlyFee + implementationFee;
      } else {
        const fee = Number(charge.fee) || 0;
        chargeTotal = fee;
      }

      const discount = Number(charge.discount) || 0;
      return total + chargeTotal - discount;
    }, 0);
  };

  const handleClose = () => {
    reset();
    setShouldScrollToLast(false);
    onClose();
  };

  const handleAddCharge = () => {
    append({
      chargeType: "",
      employeeName: "",
      fee: undefined,
      monthlyFee: undefined,
      implementationFee: undefined,
      discount: undefined,
    });

    setShouldScrollToLast(true);
  };

  const handleRemoveCharge = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const transformFormDataToApiFormat = (formData) => {
    const items = formData.charges.map((charge) => {
      const baseItem = {
        type: charge.chargeType,
        discount: Number(charge.discount) || 0,
        notes: charge.notes,
        employeeName: charge.employeeName,
      };

      if (isHolidayCompensation(charge.chargeType)) {
        if (charge.startDate && moment(charge.startDate).isValid()) {
          baseItem.startDate = moment(charge.startDate).format("YYYY-MM-DD");
        }

        if (charge.endDate && moment(charge.endDate).isValid()) {
          baseItem.endDate = moment(charge.endDate).format("YYYY-MM-DD");
        }
      }

      if (isUpsellCharge(charge.chargeType)) {
        return {
          ...baseItem,
          implementationFee: Number(charge.implementationFee) || 0,
          fee: Number(charge.monthlyFee) || 0,
        };
      }

      return {
        ...baseItem,
        fee: Number(charge.fee) || 0,
      };
    });

    return { items };
  };

  const onSubmit = (formData) => {
    if (!customerId) {
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: "Customer ID not found. Please log in again.",
          dispatch,
        })
      );
      return;
    }

    const apiData = transformFormDataToApiFormat(formData);
    setFormDataForConfirm(apiData);
    setShowConfirmModal(true);
  };

  const handleError = (error) => {
    let defaultErrorMessage =
      error?.response?.payload?.error?.[0]?.description || error?.message || "Failed to create one-time charge";
    const isNetworkError =
      !navigator.onLine ||
      error.code === "NETWORK_ERROR" ||
      error.name === "NetworkError" ||
      error.code === "ENOTFOUND" ||
      error.message?.includes("fetch") ||
      error.request;

    if (isNetworkError) {
      defaultErrorMessage = "Failed to create one-time charge, please check you internet connection";
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: defaultErrorMessage,
          dispatch,
        })
      );

      setShowConfirmModal(false);
      onClose();
      setShowErrorModal(true);
    } else {
      setErrorMessage(defaultErrorMessage);
      setShowConfirmModal(false);

      setShowErrorModal(true);
    }
  };

  const handleConfirmCharge = async () => {
    try {
      setSubmitting(true);

      if (!navigator.onLine) {
        throw new Error("No internet connection");
      }

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout")), 10000);
      });

      const response = await Promise.race([createOneTimeCharge(customerId, formDataForConfirm), timeoutPromise]);

      if (response?.status >= 400 || !response.ok) {
        const errorMsg = response?.payload?.error?.[0]?.description || response?.message || "Server error occurred";
        throw new Error(errorMsg);
      }

      setShowConfirmModal(false);
      setShowSuccessModal(true);
      onConfirm(response);
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setFormDataForConfirm(null);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    handleClose();
  };

  const handleViewCharge = () => {
    setShowSuccessModal(false);
    handleClose();
    if (onViewCharge) {
      onViewCharge();
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
  };

  const handleCreateNewCharge = () => {
    setShowErrorModal(false);
    setErrorMessage("");
    setFormDataForConfirm(null);
    reset();
  };

  const isUpsellCharge = (chargeType) => chargeType === ONE_TIME_INVOICE_ITEM_TYPE.Upsell;
  const isHolidayCompensation = (chargeType) => chargeType === ONE_TIME_INVOICE_ITEM_TYPE.HolidayCompensation;

  const isFormValid = () => {
    if (!watchedCharges || !watchedCharges.length) return false;

    return watchedCharges.every((charge) => {
      const { chargeType, employeeName, monthlyFee, implementationFee, fee, startDate, endDate } = charge;

      if (!chargeType) return false;

      if (isUpsellCharge(chargeType)) {
        return !!employeeName && (Number(monthlyFee) > 0 || Number(implementationFee) > 0);
      }

      if (isHolidayCompensation(chargeType)) {
        if (startDate && endDate && moment(startDate).isAfter(endDate)) {
          return false;
        }
        return !!employeeName && Number(fee) > 0 && !!startDate;
      }

      return Number(fee) > 0;
    });
  };

  const formIsValid = isFormValid();

  return (
    <>
      <Dialog
        open={open && !showConfirmModal && !showSuccessModal && !showErrorModal}
        onClose={handleClose}
        size="md"
        title={
          <DialogTitle
            title={<div className="heading-h2-semibold color-text-heading-primary">Create One-time Charge</div>}
          />
        }
        content={
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <DialogContent>
                <div className="create-charge-modal-content">
                  <div className="charge-modal-types">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="charge-item"
                        ref={index === fields.length - 1 ? lastChargeItemRef : null}
                      >
                        <Grid container sx={{
                          alignItems: "normal"
                        }}>
                          <Grid
                            container
                            className="charge-item-header"
                            size={{
                              xs: 12,
                              md: 11
                            }}>
                            <Grid
                              size={{
                                xs: 12,
                                md: 4
                              }}>
                              <Select
                                name={`charges.${index}.chargeType`}
                                label="Charge type"
                                placeholder="Select charge type"
                                options={allChargeTypeOptions}
                                rules={{ required: true }}
                              />
                            </Grid>

                            {(isHolidayCompensation(watchedCharges[index]?.chargeType) ||
                              isUpsellCharge(watchedCharges[index]?.chargeType)) && (
                              <Grid
                                size={{
                                  xs: 12,
                                  md: 4
                                }}>
                                <Text
                                  name={`charges.${index}.employeeName`}
                                  label="Employee name"
                                  placeholder="john doe"
                                  rules={{
                                    required: true,
                                  }}
                                  onlyAlphabets
                                />
                              </Grid>
                            )}

                            {isHolidayCompensation(watchedCharges[index]?.chargeType) && (
                              <>
                                <Grid
                                  size={{
                                    xs: 12,
                                    md: 4
                                  }}>
                                  <CustomDatePicker
                                    name={`charges.${index}.startDate`}
                                    label="Start date"
                                    placeholder="Select start date"
                                    isRequired
                                  />
                                </Grid>
                                <Grid
                                  size={{
                                    xs: 12,
                                    md: 4
                                  }}>
                                  <CustomDatePicker
                                    name={`charges.${index}.endDate`}
                                    label="End date"
                                    placeholder="Select end date"
                                  />
                                </Grid>
                              </>
                            )}

                            {isUpsellCharge(watchedCharges[index]?.chargeType) ? (
                              <>
                                <Grid
                                  size={{
                                    xs: 12,
                                    md: 4
                                  }}>
                                  <Text
                                    name={`charges.${index}.implementationFee`}
                                    label="Implementation fee"
                                    placeholder="100"
                                    type="number"
                                    startIcon={<Typography>$</Typography>}
                                    rules={{
                                      validate: (value) =>
                                        Number(value) > 0 ||
                                        Number(watchedCharges[index]?.monthlyFee) > 0 ||
                                        "At least one fee is required",
                                    }}
                                  />
                                </Grid>
                                <Grid
                                  size={{
                                    xs: 12,
                                    md: 4
                                  }}>
                                  <Text
                                    name={`charges.${index}.monthlyFee`}
                                    label="Monthly fee"
                                    placeholder="2000"
                                    type="number"
                                    startIcon={<Typography>$</Typography>}
                                    helperTextWithoutError="This fee may be prorated based on the employee's start date."
                                    rules={{
                                      validate: (value) =>
                                        Number(value) > 0 ||
                                        Number(watchedCharges[index]?.implementationFee) > 0 ||
                                        "At least one fee is required",
                                    }}
                                  />
                                </Grid>
                              </>
                            ) : (
                              <Grid
                                size={{
                                  xs: 12,
                                  md: 4
                                }}>
                                <Text
                                  name={`charges.${index}.fee`}
                                  label="Fee"
                                  placeholder="500"
                                  type="number"
                                  startIcon={<Typography>$</Typography>}
                                  rules={{
                                    required: true,
                                    min: {
                                      value: 0.01,
                                      message: "Fee must be greater than 0",
                                    },
                                  }}
                                />
                              </Grid>
                            )}

                            <Grid
                              size={{
                                xs: 12,
                                md: 4
                              }}>
                              <Text
                                name={`charges.${index}.discount`}
                                label="Discount"
                                placeholder="10"
                                type="number"
                                isOptional
                                startIcon={<Typography>$</Typography>}
                                minValue={0}
                              />
                            </Grid>

                            <Grid
                              size={{
                                xs: 12,
                                md: 12
                              }}>
                              <Text
                                name={`charges.${index}.notes`}
                                label="Notes (optional)"
                                placeholder="Add info here"
                              />
                            </Grid>
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              md: 1
                            }}>
                            {fields.length > 1 && (
                              <div className="remove-charge-icon">
                                <CloseIcon onClick={() => handleRemoveCharge(index)} className="remove-icon" />
                              </div>
                            )}
                          </Grid>
                        </Grid>
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="text"
                    size="medium"
                    onClick={handleAddCharge}
                    startIcon={<AddIcon />}
                    label="Add another charge"
                    className="add-charge-btn"
                  />

                  <div className="total-section">
                    <Box className="total-amount">
                      <Typography variant="h4" className="total-label">
                        Total amount
                      </Typography>
                      <Typography variant="h3" className="total-value">
                        ${calculateTotal().toFixed(2)}
                      </Typography>
                    </Box>
                  </div>
                </div>
              </DialogContent>
            </form>
          </FormProvider>
        }
        actions={
          <DialogActions
            action1={<Button variant="text" size="large" onClick={handleClose} label="Cancel" />}
            action2={
              <Button
                variant="contained"
                size="large"
                type="submit"
                onClick={handleSubmit(onSubmit)}
                isDisabled={!formIsValid}
                label="Continue"
              />
            }
          />
        }
      />
      <ConfirmChargeModal
        open={showConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmCharge}
        chargeData={formDataForConfirm?.items || []}
        isLoading={submitting}
      />
      <ResultModal
        open={showSuccessModal}
        onClose={handleCloseSuccessModal}
        onAction={handleViewCharge}
        type="success"
      />
      <ResultModal
        open={showErrorModal}
        onClose={handleCloseErrorModal}
        onAction={handleCreateNewCharge}
        type="error"
        errorMessage={errorMessage}
      />
    </>
  );
}

CreateOneTimeChargeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onViewCharge: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default CreateOneTimeChargeModal;
